/**
 * Cores por papel, num lugar so.
 *
 * Os estados dizem como algo esta: `success` para o positivo (emitida, aprovado,
 * ativo), `warning` para prazo e pendencia, `danger` para recusado, vencido e
 * declinado, `info` para andamento. As categorias (`violet`, `cyan`, `indigo`, `teal`,
 * `rose`) so separam coisas do mesmo nivel, como uma esteira da outra, e nao dizem
 * se algo esta bem ou mal. `brand` e a cor da empresa e `neutral` e o cinza frio.
 *
 * Os valores moram nos tokens (`styles/tokens.css`). Aqui ficam as classes, escritas
 * por extenso para que o Tailwind as encontre: montar a classe por concatenacao a
 * deixaria fora do CSS gerado.
 *
 * A cor nunca e o unico sinal. O texto, o rotulo ou o numero continuam ao lado dela.
 */
export type ToneName =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'violet'
  | 'cyan'
  | 'indigo'
  | 'teal'
  | 'rose';

/**
 * Chip de icone: fundo em degrade suave, contorno fino e o icone no passo que passa
 * de 3:1 sobre o fundo.
 */
export const chipTone: Record<ToneName, string> = {
  neutral: 'from-ink-50 to-ink-100 text-ink-600 ring-ink-200',
  brand: 'from-brand-50 to-brand-100 text-brand-600 ring-brand-200/70',
  success: 'from-success-50 to-success-100 text-success-600 ring-success-200/70',
  warning: 'from-warning-50 to-warning-100 text-warning-700 ring-warning-200/80',
  danger: 'from-danger-50 to-danger-100 text-danger-600 ring-danger-200/70',
  info: 'from-info-50 to-info-100 text-info-600 ring-info-200/70',
  violet: 'from-violet-50 to-violet-100 text-violet-600 ring-violet-200/70',
  cyan: 'from-cyan-50 to-cyan-100 text-cyan-700 ring-cyan-200/80',
  indigo: 'from-indigo-50 to-indigo-100 text-indigo-600 ring-indigo-200/70',
  teal: 'from-teal-50 to-teal-100 text-teal-700 ring-teal-200/80',
  rose: 'from-rose-50 to-rose-100 text-rose-600 ring-rose-200/70',
};

/** Cor solida, do ponto da etiqueta e da faixa da coluna. */
export const solidTone: Record<ToneName, string> = {
  neutral: 'bg-ink-400',
  brand: 'bg-brand-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  info: 'bg-info-500',
  violet: 'bg-violet-500',
  cyan: 'bg-cyan-500',
  indigo: 'bg-indigo-500',
  teal: 'bg-teal-500',
  rose: 'bg-rose-500',
};

/** Texto no tom, com contraste AA sobre o branco e sobre o fundo 50. */
export const textTone: Record<ToneName, string> = {
  neutral: 'text-ink-900',
  brand: 'text-brand-700',
  success: 'text-success-700',
  warning: 'text-warning-700',
  danger: 'text-danger-700',
  info: 'text-info-700',
  violet: 'text-violet-700',
  cyan: 'text-cyan-700',
  indigo: 'text-indigo-700',
  teal: 'text-teal-700',
  rose: 'text-rose-700',
};

/**
 * Cartao em destaque: o fundo ganha um veu do tom e a borda esquerda uma faixa (o
 * pseudo-elemento que o componente posiciona). Serve ao indicador que pede acao
 * agora, como o prazo vencido maior que zero.
 */
export const highlightTone: Record<ToneName, string> = {
  neutral: 'from-ink-50 border-ink-200 before:bg-ink-400',
  brand: 'from-brand-50 border-brand-200 before:bg-brand-500',
  success: 'from-success-50 border-success-200 before:bg-success-500',
  warning: 'from-warning-50 border-warning-200 before:bg-warning-500',
  danger: 'from-danger-50 border-danger-200 before:bg-danger-500',
  info: 'from-info-50 border-info-200 before:bg-info-500',
  violet: 'from-violet-50 border-violet-200 before:bg-violet-500',
  cyan: 'from-cyan-50 border-cyan-200 before:bg-cyan-500',
  indigo: 'from-indigo-50 border-indigo-200 before:bg-indigo-500',
  teal: 'from-teal-50 border-teal-200 before:bg-teal-500',
  rose: 'from-rose-50 border-rose-200 before:bg-rose-500',
};

/**
 * Brilho no canto do cartao, no tom do icone: a cor chega ao cartao sem disputar
 * com o numero.
 */
export const glowTone: Record<ToneName, string> = {
  neutral:
    'bg-[radial-gradient(circle_at_100%_0%,var(--color-ink-50),transparent_55%)]',
  brand:
    'bg-[radial-gradient(circle_at_100%_0%,var(--color-brand-50),transparent_55%)]',
  success:
    'bg-[radial-gradient(circle_at_100%_0%,var(--color-success-50),transparent_55%)]',
  warning:
    'bg-[radial-gradient(circle_at_100%_0%,var(--color-warning-50),transparent_55%)]',
  danger:
    'bg-[radial-gradient(circle_at_100%_0%,var(--color-danger-50),transparent_55%)]',
  info: 'bg-[radial-gradient(circle_at_100%_0%,var(--color-info-50),transparent_55%)]',
  violet:
    'bg-[radial-gradient(circle_at_100%_0%,var(--color-violet-50),transparent_55%)]',
  cyan: 'bg-[radial-gradient(circle_at_100%_0%,var(--color-cyan-50),transparent_55%)]',
  indigo:
    'bg-[radial-gradient(circle_at_100%_0%,var(--color-indigo-50),transparent_55%)]',
  teal: 'bg-[radial-gradient(circle_at_100%_0%,var(--color-teal-50),transparent_55%)]',
  rose: 'bg-[radial-gradient(circle_at_100%_0%,var(--color-rose-50),transparent_55%)]',
};

/**
 * Veu de categoria, para o quadro de numero que nao pede acao (como os cartoes dos
 * relatorios): o fundo no tom, o contorno claro e a faixa fina em cima. A faixa de
 * cima, e nao a da esquerda, separa a categoria do destaque de acao (`highlightTone`).
 */
export const tintTone: Record<ToneName, string> = {
  neutral:
    'from-ink-50 to-ink-100/40 border-ink-200/70 shadow-[inset_0_3px_0_var(--color-ink-400)]',
  brand:
    'from-brand-50 to-brand-100/40 border-brand-200/70 shadow-[inset_0_3px_0_var(--color-brand-500)]',
  success:
    'from-success-50 to-success-100/40 border-success-200/70 shadow-[inset_0_3px_0_var(--color-success-500)]',
  warning:
    'from-warning-50 to-warning-100/40 border-warning-200/70 shadow-[inset_0_3px_0_var(--color-warning-500)]',
  danger:
    'from-danger-50 to-danger-100/40 border-danger-200/70 shadow-[inset_0_3px_0_var(--color-danger-500)]',
  info: 'from-info-50 to-info-100/40 border-info-200/70 shadow-[inset_0_3px_0_var(--color-info-500)]',
  violet:
    'from-violet-50 to-violet-100/40 border-violet-200/70 shadow-[inset_0_3px_0_var(--color-violet-500)]',
  cyan: 'from-cyan-50 to-cyan-100/40 border-cyan-200/70 shadow-[inset_0_3px_0_var(--color-cyan-500)]',
  indigo:
    'from-indigo-50 to-indigo-100/40 border-indigo-200/70 shadow-[inset_0_3px_0_var(--color-indigo-500)]',
  teal: 'from-teal-50 to-teal-100/40 border-teal-200/70 shadow-[inset_0_3px_0_var(--color-teal-500)]',
  rose: 'from-rose-50 to-rose-100/40 border-rose-200/70 shadow-[inset_0_3px_0_var(--color-rose-500)]',
};

/**
 * Categorias em ordem fixa, para dar a cada item de uma lista uma cor propria sem
 * tomar emprestado o sentido de um estado. A marca vem primeiro.
 */
export const categoryTones: readonly ToneName[] = [
  'brand',
  'violet',
  'cyan',
  'indigo',
  'teal',
  'rose',
];

/** A categoria da posicao, repetindo a ordem quando a lista passa de seis. */
export function categoryTone(index: number): ToneName {
  const tamanho = categoryTones.length;
  return categoryTones[((index % tamanho) + tamanho) % tamanho] ?? 'brand';
}
